/**
 * In-app DASH brochure content — sourced from dash-api/docs/BROCHURE.md
 */
export const DASH_BROCHURE_MARKDOWN = `# DASH

**Discovery, Analysis, Summaries & Highlights**

**The intelligence platform for evidence-backed analysis — from collection to publication.**

DASH brings collection, drafting, review, and publication into one workspace. Analysts write with confidence: every claim can be tied to sources, tradecraft checks run as they work, and teams collaborate in real time without losing attribution or control.

---

## The challenge

Intelligence organizations lose time and quality when analysis is spread across disconnected tools — separate search portals, word processors, citation managers, review trackers, and ad hoc AI assistants. Evidence gets orphaned. Tradecraft issues surface late in review. Collaboration erases credit. Classification mistakes slip through.

**DASH replaces the patchwork with a single, purpose-built platform.**

---

## What DASH does

DASH supports the full analytic lifecycle:

1. **Collect** — Gather requirements, issue collection requests, and ingest source material from virtually any format — documents, cables, media, and web pages — including OSINT, dark web, and social network feeds.
2. **Analyze** — Draft intelligence products in a rich editor with AI-assisted claim checking, entity linking, and semantic search across your corpus.
3. **Validate** — Run automated tradecraft and compliance checks; reviewers validate products in dedicated workflows.
4. **Publish** — Produce finished products (FINTELs, SITREPs, disclosures) with full provenance and reusable knowledge.

---

## Why organizations choose DASH

### Write with evidence, not after the fact

Analysts draft in a write-first workflow. DASH identifies claims in the text and surfaces **supporting and contradicting evidence** from the attached source package — while the author is still writing, not after a separate review pass.

### Built-in tradecraft quality

**ICD-203** and analytical best-practices checks run against the draft and flag passages that need clearer sourcing or logic. Actionable feedback reduces review cycles and raises baseline quality across the organization.

### Pressure-test your judgments

On-demand **alternative hypothesis** generation draws from the same source material to counter confirmation bias — a structured challenge step that used to take hours now takes minutes.

### One workspace, not five tools

Search the corpus, manage sources, draft, cite, link entities, run checks, and collaborate — **without switching applications**. Source packages, citations, and classification markings stay synchronized with the document.

### Collaboration that preserves credit

Multiple analysts edit the same product simultaneously. **Character-level attribution** tracks who contributed what — essential for reuse, review, and credit in shared analytic environments.

### Knowledge that compounds

Entity linking, tags, and a shared **Knowledge Base** make finished analysis discoverable and reusable. Today's product becomes tomorrow's starting point.

### Role-specific experiences

Every persona gets an interface tuned to their job — analysts draft, reviewers validate, collection managers track ICRs, disclosure officers review public products, and managers monitor operational health.

---

## Any format. Any language. Any source.

### Rich content ingestion

DASH is built to absorb the full range of material analysts work with — not just PDFs. Upload or import once; the platform extracts text, structure, and metadata automatically so every source becomes searchable and citable in the editor.

| Format | What DASH does with it |
|--------|------------------------|
| **PDF** | Layout-aware extraction with synchronized source ↔ text viewer |
| **DOCX** | Word documents converted to structured, editable content |
| **CABLE** | Intelligence cable format parsed with routing metadata preserved |
| **MP3 / audio** | Transcription to searchable text with timeline alignment |
| **MP4 / video** | Transcription and metadata extraction; jump to moments in the source |
| **Web pages** | URL import with article extraction, image re-hosting, and formatting fidelity |
| **Images** | Stored and linked alongside related text sources |
| **Maps** | Geospatial data and imagery ingested and linked to analytic products |

### Live translation for multilingual sources

Foreign-language material is no longer a dead end. DASH **detects source language at ingest** and **translates to English** as part of the processing pipeline. Analysts work in a **dual-pane viewer** — original language alongside English — so they can verify meaning against the source while drafting in their working language.

### Unified access to diverse intelligence sources

Stop searching in silos. DASH brings material from **multiple collection domains into one federated corpus**:

| Source domain | Examples |
|---------------|----------|
| **OSINT** | Open-source web content, news, public records, academic sources |
| **Dark web / darknet** | Monitored dark-web collections ingested as structured corpus items |
| **Social networks** | Social media posts, profiles, and thread content from collection feeds |
| **Proprietary & internal** | Agency uploads, partner shares, and classified document stores |
| **User workspace** | Analyst-uploaded files, highlights, and saved search results |

Every item carries **provenance** — where it came from, who uploaded it, and how it was processed.

---

## Custom workflows and full observability

### Design workflows that match your organization

DASH includes a **visual Workflow Designer** where managers and administrators build and customize analytic pipelines — add steps, connect stages, label handoffs, and map how PITCHes become ICRs, drafts, reviews, FINTELs, and disclosures.

### Complete visibility into resources and utilization

| Area | What leaders and operators see |
|------|-------------------------------|
| **User activity** | Sessions, time on reports, searches, uploads, creations by type |
| **AI usage and costs** | Usage volumes, spend trends, and audit trails for AI-assisted analysis |
| **Background processing** | Task queue status, job progress, retries, and dead-letter visibility |
| **Platform health** | Service health probes across API, sidecars, and external dependencies |
| **Corpus operations** | Data-item and report processing stats, reprocess controls, backfill status |
| **Resource management** | Admin operations for corpus, entities, storage, backups, and user administration |

---

## Live chat and collaboration

| Capability | What it delivers |
|------------|------------------|
| **Live team chat** | Real-time 1:1 messaging between analysts via the floating communication hub |
| **Expert & FINTEL chat** | Dedicated chat flows for subject-matter experts and FINTEL analysis threads |
| **AI assistant chat** | Document-aware conversational support, global or scoped to a specific product |
| **Real-time document collaboration** | Multiple analysts editing the same report with live cursors and character-level attribution |
| **Collaborative whiteboards** | Shared visual workspace for mapping ideas, entities, and relationships |

*Planned:* screen sharing and multi-seat video conferencing for live review sessions.

---

## Product types

| Product | What it is |
|---------|------------|
| **Pitch** | Early analytic concept for stakeholder alignment |
| **FINTEL** | Finished intelligence analytic product |
| **SITREP** | Situation report for time-sensitive updates |
| **Disclosure** | Public-facing product with a dedicated review path |

---

## Key capabilities at a glance

| Capability | Benefit |
|------------|---------|
| **Multi-format ingestion** | PDF, DOCX, CABLE, audio, video, web pages, maps, and more |
| **Live translation** | Non-English sources auto-translated; dual-pane original ↔ English viewer |
| **Multi-source corpus** | OSINT, darknet, social networks, and proprietary feeds in one search index |
| **Collaborative editor** | Real-time coauthoring with version history |
| **Source packages & citations** | Fragment-level evidence tied to every statement |
| **Semantic & AI search** | Find relevant material across the entire corpus |
| **Collection requests (ICR)** | Track what to collect and measure coverage |
| **Entity & knowledge base** | Consistent names, links, and reusable entity records |
| **Whiteboards** | Visual collaboration alongside written analysis |
| **Live chat & collaboration** | Team messaging, expert chat, and real-time coauthoring in one hub |
| **AI assistant** | Document-aware chat for analysis support |
| **Review workflows** | Structured validation before approval |
| **Custom workflow designer** | Visual pipelines tailored to your organization's production model |
| **Operational Insights** | Usage trends, team activity, and production metrics for managers |
| **Full observability** | User activity, AI usage costs, task queues, and platform health |

---

## Who DASH is for

| Audience | How DASH helps |
|----------|----------------|
| **Analysts** | Draft faster with evidence and tradecraft support at the point of writing |
| **Reviewers** | Validate products with full preview and structured review tools |
| **Collection managers** | Manage ICRs and track how products cover requirements |
| **Policymakers** | Drive the requirements pipeline from IR through collection |
| **Disclosure officers** | Review public products in a dedicated workflow |
| **Managers & leaders** | Operational Insights — visibility into production, team activity, and platform usage |

---

## Platform overview

DASH is delivered as an integrated platform:

- **Analyst application** — The primary workspace for products, sources, search, knowledge base, and collaboration.
- **Operations console** — User administration, platform health, and usage monitoring for administrators.
- **Knowledge graph** — Investigative collections, graph exploration, and structured QA for deep-dive analysis.

All components share a unified backend — one source of truth for users, documents, entities, and audit trails.

---

## The DASH difference

| Traditional approach | With DASH |
|---------------------|-----------|
| Search in one tool, write in another | Search and write in the same workspace |
| Tradecraft review at the end | Continuous tradecraft coaching while drafting |
| Manual citation and evidence tracking | AI-assisted claim-to-evidence linking |
| Email-based collaboration | Real-time coauthoring with attribution |
| Siloed entity names and tags | Shared knowledge base and entity linking |
| Siloed collections per source type | OSINT, darknet, social, and uploads in one searchable corpus |
| Foreign-language sources handled offline | Automatic translation with side-by-side source verification |
| Ad hoc AI chatbots | Document-aware AI grounded in your sources |

---

*DASH — Discovery, Analysis, Summaries & Highlights. Draft with evidence. Publish with confidence.*
`;
